import {
  getServerUrl,
  getAnchorServerUrl,
} from "@trex-2b/libs/config/server-config";
import { Api as TrexApi } from "@trex-2b/api/src/trex-api/trex-api";
import { TrexApiProxy } from "@trex-2b/api/src/trex-api/trex-api-proxy";
import { Api as TrexAnchorApi } from "@trex-2b/api/src/trex-anchor-api/trex-anchor-api";
import { TrexAnchorApiProxy } from "@trex-2b/api/src/trex-anchor-api/trex-anchor-api-proxy";
import { Api as AegisApi } from "@trex-2b/api/src/regis-api/regis-api";
import { AegisApiProxy } from "@trex-2b/api/src/regis-api/regis-api-proxy";
import {
  clearLoginTokens,
  clearPassportInfo,
  getStoredLoginTokens,
  subscribeCrossTabLogout,
  type LoginTokens,
  saveLoginTokenCookies,
  saveLoginTokens,
} from "./token-store";

const trexClient = new TrexApi();
const anchorClient = new TrexAnchorApi();
const aegisClient = new AegisApi();
const anchorServerUrl = getAnchorServerUrl();

/** Initial Anchor `chain_id` before wallet connects; override with `setTrexAnchorChainId` from Wagmi. */
const initialAnchorChainId =
  typeof process !== "undefined"
    ? (process.env.NEXT_PUBLIC_DEFAULT_ANCHOR_CHAIN_ID ?? "")
    : "";

export const trexAuthProxy = new TrexApiProxy(trexClient, getServerUrl());
export const aegisAuthProxy = new AegisApiProxy(
  aegisClient,
  trexAuthProxy,
  getServerUrl(),
);
export const trexAnchorAuthProxy = new TrexAnchorApiProxy(
  anchorClient,
  initialAnchorChainId,
  anchorServerUrl,
  trexAuthProxy,
);

/**
 * Keep Anchor API request headers in sync with the user’s active EVM chain.
 * Call from a client component under `WagmiProvider` (e.g. `useChainId` + `useEffect`).
 * chain配置同步Anchor api配置
 */
export function setTrexAnchorChainId(
  chainId: string | number | bigint | null | undefined,
) {
  const s =
    chainId === null || chainId === undefined || chainId === ""
      ? ""
      : String(chainId);
  trexAnchorAuthProxy.setChainId(s);
}

let initialized = false;

export const initializeTrexAuth = () => {
  if (initialized) return;
  initialized = true;

  const tokens = getStoredLoginTokens();
  if (tokens) {
    trexAuthProxy.setToken(tokens.accessToken);
    trexAnchorAuthProxy.setToken(tokens.accessToken);
    trexAuthProxy.setRefreshToken(tokens.refreshToken);
    trexAnchorAuthProxy.setRefreshToken(tokens.refreshToken);
  }

  trexAuthProxy.setOnTokenRefreshFailed(() => {
    clearTrexAuth();
  });
  trexAuthProxy.setShouldAttemptTokenRefresh(() =>
    Boolean(getStoredLoginTokens()),
  );
  trexAuthProxy.setOnTokenRefreshed((tokens) => {
    if (!getStoredLoginTokens()) {
      return;
    }
    saveLoginTokenCookies(tokens);
  });
  subscribeCrossTabLogout(() => {
    clearTrexAuth({ broadcastLogout: false });
  });
};

export const setTrexAuthTokens = (tokens: LoginTokens) => {
  trexAuthProxy.beginAuthSession();
  saveLoginTokens(tokens);
  trexAuthProxy.setToken(tokens.accessToken);
  trexAnchorAuthProxy.setToken(tokens.accessToken);
  trexAuthProxy.setRefreshToken(tokens.refreshToken);
  trexAnchorAuthProxy.setRefreshToken(tokens.refreshToken);
};

export const clearTrexAuth = (
  options: { broadcastLogout?: boolean } = { broadcastLogout: true },
) => {
  clearLoginTokens({ broadcastLogout: options.broadcastLogout !== false });
  clearPassportInfo();
  trexAuthProxy.clearToken();
  trexAnchorAuthProxy.clearToken();
};

/** 供 Route / useChat transport 读取当前 Trex access token（与 trexAuthProxy 会话一致）。 */
export function getCurrentTrexToken(): string {
  const stored = getStoredLoginTokens();
  if (stored?.accessToken?.trim()) {
    return stored.accessToken.trim();
  }
  return trexAuthProxy.token?.trim() ?? "";
}
